import { markdownExcerpt, type HighlightColor, type LibraryView } from "@bibleunleashed/shared";
import { useState, type FormEvent } from "react";
import { Link, Redirect } from "wouter";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { put } from "@/lib/api";
import { useLogout, useUser } from "@/lib/auth";
import { useLibrary } from "@/lib/library";
import { useConnections, useDisconnect } from "@/lib/oauth";

export function Account() {
  const { data: user, isLoading } = useUser();
  const logout = useLogout();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [state, setState] = useState<{ status: "idle" | "saving" | "done" | "error"; message?: string }>({ status: "idle" });

  if (isLoading) return null;
  if (!user) return <Redirect to="/login" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState({ status: "saving" });
    try {
      await put("/api/auth/password", user.hasPassword ? { currentPassword: current, newPassword: next } : { newPassword: next });
      setCurrent("");
      setNext("");
      setState({ status: "done" });
    } catch (err) {
      setState({ status: "error", message: err instanceof Error ? err.message : "Could not change the password" });
    }
  };

  const field = "mt-1 w-full rounded-md border border-rule bg-paper-2 px-3 py-2 text-[14px] text-ink focus:border-accent focus-visible:outline-none";
  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[640px] px-6 py-16">
        <h1 className="font-serif text-[28px] font-semibold">Account</h1>
        <dl className="mt-6 border-y border-rule py-4 text-[14px]">
          <div className="flex justify-between py-1">
            <dt className="text-ink-3">Name</dt>
            <dd>{user.name}</dd>
          </div>
          <div className="flex justify-between py-1">
            <dt className="text-ink-3">Email</dt>
            <dd>{user.email}</dd>
          </div>
          <div className="flex justify-between py-1">
            <dt className="text-ink-3">Role</dt>
            <dd>{user.role === "admin" ? "Administrator — the site’s one editor" : "Member"}</dd>
          </div>
        </dl>

        {user.role === "admin" && (
          <p className="mt-5 flex flex-wrap gap-x-5 text-[13px]">
            <Link href="/commentary" className="text-accent">Write commentary →</Link>
            <Link href="/stats" className="text-accent">Site statistics →</Link>
          </p>
        )}

        {user.role === "admin" && <ClaudeConnections />}

        <MyLibrary />

        {user.passwordFromEnv ? (
          <>
            <h2 className="mt-12 font-serif text-[20px] font-semibold">Password</h2>
            <p className="mt-1 text-[13px] text-ink-2">
              Yours is <code>ADMIN_PASSWORD</code> in the server’s environment (the Dokploy panel). Change it there and
              redeploy; the new one takes effect as the server starts, and signs every device out.
            </p>
          </>
        ) : (
          <>
            <h2 className="mt-12 font-serif text-[20px] font-semibold">
              {user.hasPassword ? "Change password" : "Set a password"}
            </h2>
            <p className="mt-1 text-[13px] text-ink-2">
              {user.hasPassword
                ? "Signs out every other device."
                : "You don’t need one — an email link signs you in just as well. Set one if you would rather not wait for email."}
            </p>
            <form onSubmit={submit} className="mt-4 flex max-w-sm flex-col gap-4">
              {user.hasPassword && (
                <label className="text-[12px] tracking-[0.06em] text-ink-3 uppercase">
                  Current password
                  <input className={field} type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
                </label>
              )}
              <label className="text-[12px] tracking-[0.06em] text-ink-3 uppercase">
                New password
                <input className={field} type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={10} autoComplete="new-password" />
              </label>
              {state.status === "error" && <p className="text-[13px] text-accent">{state.message}</p>}
              {state.status === "done" && <p className="text-[13px] text-ink-2">Password saved.</p>}
              <button disabled={state.status === "saving"} className="rounded-md bg-accent px-4 py-2 text-[14px] font-medium text-white disabled:opacity-60">
                {state.status === "saving" ? "Saving…" : user.hasPassword ? "Change password" : "Set password"}
              </button>
            </form>
          </>
        )}

        <button onClick={() => logout.mutate()} className="mt-8 text-[13px] text-ink-2 hover:text-ink">Sign out</button>
      </main>
    </div>
  );
}

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/**
 * Claude, wherever the admin connected it by OAuth — claude.ai and the phone
 * app, or Claude Code. Disconnecting deletes its token at once. MCP_TOKEN is
 * not listed: it lives in the server's environment and is changed there.
 */
function ClaudeConnections() {
  const { data, isLoading, error } = useConnections(true);
  const disconnect = useDisconnect();
  const connections = data ?? [];

  return (
    <section className="mt-12">
      <h2 className="font-serif text-[20px] font-semibold">Claude connections</h2>
      <p className="mt-1 text-[13px] text-ink-2">
        Each can edit the site as you. Add one from claude.ai’s connector settings with the address{" "}
        <code>{window.location.origin}/mcp</code>.
      </p>
      {isLoading && <p className="mt-2 text-[13px] text-ink-3">Loading…</p>}
      {error && <p className="mt-2 text-[13px] text-ink-2">Couldn’t load them: {error.message}</p>}
      {!isLoading && !error && connections.length === 0 && <p className="mt-2 text-[13px] text-ink-3">None connected.</p>}
      {connections.length > 0 && (
        <ul className="mt-3 divide-y divide-rule border-y border-rule">
          {connections.map((c) => (
            <li key={c.id} className="flex items-baseline gap-3 py-2.5 text-[14px]">
              <span className="font-medium">{c.client}</span>
              <span className="text-[12px] text-ink-3">
                connected {day(c.connectedAt)}
                {c.lastUsedAt && ` · last used ${day(c.lastUsedAt)}`}
              </span>
              <button
                onClick={() => disconnect.mutate(c.id)}
                disabled={disconnect.isPending}
                className="ml-auto text-[13px] text-accent disabled:opacity-60"
              >
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      )}
      {disconnect.error && <p className="mt-2 text-[13px] text-accent">{disconnect.error.message}</p>}
    </section>
  );
}

const DOT: Record<HighlightColor, string> = {
  yellow: "var(--hl-y)",
  green: "var(--hl-g)",
  blue: "var(--hl-b)",
  pink: "var(--hl-p)",
};

const verseOf = (ref: string) => ref.split(".")[2] ?? "";

/**
 * Everything the member has marked, chapter by chapter. A chapter links back
 * to its page only when there is commentary published on it — without that
 * there is no page to link to, because scripture only appears here with
 * commentary on it.
 */
function MyLibrary() {
  const { data, isLoading, error } = useLibrary();
  const chapters: LibraryView["chapters"] = data?.chapters ?? [];

  return (
    <section className="mt-12">
      <h2 className="font-serif text-[20px] font-semibold">Your library</h2>
      {isLoading && <p className="mt-2 text-[13px] text-ink-3">Loading…</p>}
      {error && <p className="mt-2 text-[13px] text-ink-2">Couldn’t load it: {error.message}</p>}
      {!isLoading && !error && chapters.length === 0 && (
        <p className="mt-2 font-serif text-[15px] leading-relaxed text-ink-2">
          Nothing here yet. Right-click — or press and hold — any verse while you read to highlight it, write a note on
          it, or bookmark it. <Link href="/commentary" className="text-accent">Find a chapter →</Link>
        </p>
      )}

      <div className="mt-4 space-y-6">
        {chapters.map((chapter) => (
          <article key={chapter.osis} className="rounded-lg border border-rule px-4 py-3.5">
            <header className="flex items-baseline gap-2">
              {chapter.written ? (
                <Link href={`/commentary/${chapter.osis.replace(".", "/")}`} className="font-serif text-[17px] font-semibold text-accent">
                  {chapter.display}
                </Link>
              ) : (
                <h3 className="font-serif text-[17px] font-semibold">{chapter.display}</h3>
              )}
              <span className="text-[11.5px] text-ink-3">{summarise(chapter)}</span>
              {!chapter.written && (
                <span className="ml-auto text-[11.5px] text-ink-3 italic">not written up yet</span>
              )}
            </header>

            {chapter.notes.length > 0 && (
              <ul className="mt-3 space-y-2">
                {chapter.notes.map((note) => (
                  <li key={note.id} className="border-l-2 border-accent/40 pl-3">
                    <span className="font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">v. {verseOf(note.ref)}</span>
                    <span className="mt-0.5 block font-serif text-[14.5px] leading-snug text-ink-2">
                      {markdownExcerpt(note.bodyMd, 160)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {chapter.bookmarks.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {chapter.bookmarks.map((mark) => (
                  <li key={mark.id} className="flex items-center gap-1.5 text-[13px] text-ink-2">
                    <Icon name="bookmark" width="12" height="12" style={{ color: DOT[mark.color] }} />
                    {mark.label}
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

const counts = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "3 highlights · 1 note" — only what the chapter actually has. */
function summarise(chapter: LibraryView["chapters"][number]): string {
  return [
    [chapter.highlights.length, "highlight"] as const,
    [chapter.notes.length, "note"] as const,
    [chapter.bookmarks.length, "bookmark"] as const,
  ]
    .filter(([n]) => n > 0)
    .map(([n, word]) => counts(n, word))
    .join(" · ");
}
